<template>
    <div class="leaderboard-card"
        @click="onCardTapped"
    >
        <leaderboard-header :class="['leaderboard-card__leaderboard-header', {'leaderboard-card__leaderboard-header--add-margin-bottom': searchable}]"
            :leaderboardData="leaderboardData"
            :isSearchEnabled="searchable"
            :showParticipationAmount="showParticipationAmount"
        />
        <div v-if="searchable"
            class="leaderboard-card__search-section"
            @click="hideSearchDropdown = false"
        >
            <search-comp @onClear="searchCleared"
                @onTextChange="searchChanged"
            />

            <div v-if="isSearchInProgress && !hideSearchDropdown"
                id="leaderboard-card-find-specific-user-in-leaderboard"
                class="search-result"
            >
                <template v-if="searchedValue.length < 3">
                    <div class="title">{{ $t('rewards.leaderboards.usernameLengthTooSmall') }}</div>
                </template>

                <template v-else-if="searchedUsersList.length > 0">
                    <div v-for="user in searchedUsersList"
                        :id="`leaderboard-card-searched-user-${user.id}`"
                        :key="user.id"
                        class="user"
                        @click="findUserInLeaderboard(user)"
                    >
                        <div class="user-avatar">
                            <user-avatar class="avatar"
                                :userAvatar="user.defaultAvatarUrl"
                            />
                        </div>
                        <div id="leaderboard-card-searched-user-username"
                            class="title"
                        >
                            {{ user.username }}
                        </div>
                        <div id="leaderboard-card-searched-user-country"
                            class="subtitle"
                        >
                            {{ getUserCountry(user) }}
                        </div>
                    </div>
                </template>

                <template v-else-if="searchedValue.length > 2 && searchLoading">
                    <loading-spinner />
                </template>

                <template v-else-if="!searchedUsersList.length && !searchLoading">
                    <div class="title">{{ $t('rewards.leaderboards.noMatchingUsername') }}</div>
                </template>


                <template v-else-if="typeof searchedUser === 'undefined'">
                    <div class="not-found">
                        <div class="user-avatar">
                            <div class="user-not-found">
                                <i class="icon-search-failure" />
                            </div>
                        </div>
                        <div class="title">{{ $t('rewards.leaderboards.userNotFoundTitle') }}</div>
                        <div class="subtitle">{{ $t('rewards.leaderboards.userNotFoundSubtitle') }}</div>
                    </div>
                </template>
            </div>
        </div>
        <div v-if="userNotPartOfLeaderboard"
            class="leaderboard-card__no-participants"
        >
            {{ $t('rewards.leaderboards.userNotFoundOnLeaderboard') }}
        </div>
        <template v-else-if="participants.length > 0">
            <div id="leaderboard-card-scroll-container"
                ref="scroller"
                :class="{ scroller: detailedView }"
                @scroll="scrollLoadCheck"
            >
                <participant-item v-for="item in participantsArray"
                    :key="item.user.id"
                    :class="['leaderboard-card__participant-item', {
                        'leaderboard-card__participant-item--blur': isSearchInProgress && !hideSearchDropdown,
                        'leaderboard-card__participant-item--highlighted': searchedUser ? item.chilizUserId === searchedUser.externalUserId : false,
                        'leaderboard-card__participant-item--you': item.chilizUserId === userId,
                    }]"
                    :participantUserId="item.chilizUserId"
                    :name="item.user.username"
                    :avatar="item.user.defaultAvatarUrl"
                    :rank="item.rank"
                    :oldRank="item.oldRank"
                    :country="item.user.country"
                    :xp="item.xp"
                    :prizeList="leaderboardData.leaderboardPrize"
                    :leaderboardId="leaderboardData.id"
                />
                <div v-if="!detailedView"
                    class="leaderboard-card__bottom-gradient"
                    :style="userLateRank ? 'bottom: 4rem;' : ''"
                />
                <participant-item v-if="userLateRank"
                    :class="['leaderboard-card__user-late-rank', {'leaderboard-card__participant-item--blur': isSearchInProgress && !hideSearchDropdown}]"
                    :participantUserId="userLateRank.chilizUserId"
                    :avatar="userLateRank.avatar"
                    :name="userLateRank.username"
                    :rank="userLateRank.rank"
                    :oldRank="userLateRank.oldRank"
                    :country="userLateRank.country"
                    :xp="userLateRank.xp"
                    :prizeList="leaderboardData.leaderboardPrize"
                    :leaderboardId="leaderboardData.id"
                />
            </div>
        </template>
        <div v-else
            class="leaderboard-card__no-participants"
        >
            {{ $t('rewards.leaderboards.rankingsUnavailable') }}
        </div>
        <div id="leaderboard-card-go-to-top"
            :class="['to-top', {'to-top--visible': isScrollToTopVisible}]"
            @click="goToTop"
        >
            <span>{{ $t('global.goToTop') }}</span>
        </div>
    </div>
</template>

<script>
// 3rd party libs
import {
    mapActions,
    mapGetters,
} from 'vuex';
import Axios from 'axios';

// Components
import leaderboardHeader from '@webapp/modules/rewards/components/leaderboards/leaderboard-header.vue';
import participantItem from '@webapp/modules/rewards/components/leaderboards/participant-item.vue';
import searchComp from '@webapp/components/search.vue';
import userAvatar from '@webapp/components/user-avatar.vue';
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';

// Utils
import {
    prepareEDTOHeaders,
} from '@shared/utils/edto';

// Shared modules
import {
    api,
} from '@shared/setups/api';

// Local Types
import localStoreTypes from '@webapp/store/types.js';

// Types
import {
    ServerConfigurationProps,
} from '@shared/types/config';

let cancel = null;
let timeout = null;

export default {
    name: 'leaderboard-card',
    components: {
        leaderboardHeader,
        participantItem,
        searchComp,
        userAvatar,
        loadingSpinner,
    },
    props: {
        leaderboardData: {
            type: Object,
            default: null,
        },
        participants: {
            type: Array,
            default: () => ([]),
        },
        userLateRank: {
            type: Object,
            default: null,
        },
        detailedView: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        showParticipationAmount: {
            type: Boolean,
            default: false,
        },
        // Activate the conditions that show when we click on the leaderboard that user aren't participating in
        isConditionsApplied: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            lastScroll: 0,
            scrollerHeight: 0,
            loading: false,
            searchedValue: '',
            searchedUsersList: [],
            searchedUser: {},
            searchLoading: false,
            pageFromTo: [1, 1],
            rankingsPerPage: 20,
            userNotPartOfLeaderboard: false,
            hideSearchDropdown: true,
            participantList: null,
            paginated: false,
            paginatedUp: false,
            isScrollToTopVisible: false,
        };
    },
    computed: {
        ...mapGetters('leaderboards', [
            'getCurrentLeaderboardRankings',
            'getLeaderboardRankingsMetadata',
            'getSpecifiedUserRank',
        ]),

        ...mapGetters([
            'getSearchedUsers',
            'userId',
            'voteTokenOfferingsByStatusAndTokenId',
        ]),

        ...mapGetters('partners', ['partnerById']),

        ...mapGetters('wallet', ['decentralizedLedgerByToken']),

        ...mapGetters('countries', ['findCountryByIso2']),

        ...mapGetters('voteTokens', ['getVoteTokenByCurrencyId']),

        ...mapGetters('marketplace', ['marketByPartnerId']),

        ...mapGetters('serverConfig', ['serverConfigByKey']),

        isFreemiumLeaderboardsEnabled() {
            return this.serverConfigByKey(ServerConfigurationProps.isFreemiumLeaderboardsEnabled);
        },

        isSearchInProgress() {
            return this.searchedValue;
        },

        participantsArray() {
            return this.participantList || this.participants;
        },
    },
    watch: {
        getCurrentLeaderboardRankings(value) {
            this.loading = false;
            this.participantList = value;
            const scrollList = this.$refs.scroller;

            // When finding a user, we scroll around it's position so its immediately visible to the user
            if (this.getSpecifiedUserRank.rank && !this.paginated) {
                this.$nextTick(() => {
                    const maxScroll = scrollList.scrollHeight - scrollList.clientHeight;

                    scrollList.scrollTop =
                    ((this.getSpecifiedUserRank.rank - ((this.pageFromTo[0] - 1) * this.rankingsPerPage)) / this.rankingsPerPage) * maxScroll;
                });
            }

            // When paginating upwards, we need to scroll user down, but make it looks like the user didn't scroll
            // Otherwise the new items just pop in without looking like you scroll to them
            if (this.paginatedUp) {
                this.$nextTick(() => {
                    scrollList.scrollTop += scrollList.scrollHeight - this.scrollerHeight;
                });
            }

            this.paginated = false;
            this.paginatedUp = false;
        },
    },
    mounted() {
        if (this.$route.query.findUser) {
            this.searchedUser.externalUserId = this.userId;

            this.findUserInLeaderboard();
        }
    },
    methods: {
        ...mapActions('leaderboards', {
            searchUserRank: 'lookForUserRankByChilizUserId',
            fetchLeaderboardRankings: 'fetchLeaderboardRankings',
        }),

        ...mapActions({
            searchSimilarUsernames: 'searchSimilarUsernames',
            setModal: localStoreTypes.SET_MODAL,
        }),

        getUserCountry(user) {
            if (user.user.country && user.user.country.length === 2) {
                return this.findCountryByIso2(user.user.country).translations[0].value;
            }

            return '';
        },

        scrollLoadCheck(e) {
            const scrollList = this.$refs.scroller;
            const maxScroll = scrollList.scrollHeight - scrollList.clientHeight;
            const scrollDirectionDown = e.target.scrollTop > this.lastScroll;
            const minScrollNeededToLoad = 100;

            // Check for if we need to show "Go to top" button
            if (e.target.scrollTop > 300 || this.pageFromTo[0] > 1 || this.loading) {
                this.isScrollToTopVisible = true;
            } else {
                this.isScrollToTopVisible = false;
            }

            if (this.loading) {
                return;
            }

            // Pagination downwards
            if (scrollDirectionDown &&
                e.target.scrollTop <= maxScroll &&
                e.target.scrollTop > maxScroll - minScrollNeededToLoad &&
                this.pageFromTo[1] < this.getLeaderboardRankingsMetadata.last_page
            ) {
                this.loading = true;
                this.fetchLeaderboardRankings({
                    leaderboardId: this.leaderboardData.id,
                    page: ++this.pageFromTo[1],
                    limit: this.rankingsPerPage,
                    push: true,
                });
                this.paginated = true;
            }

            // Pagination upwards
            if (!scrollDirectionDown && e.target.scrollTop >= 0 && e.target.scrollTop < minScrollNeededToLoad && this.pageFromTo[0] > 1) {
                this.loading = true;
                this.scrollerHeight = scrollList.scrollHeight;
                this.fetchLeaderboardRankings({
                    leaderboardId: this.leaderboardData.id,
                    page: --this.pageFromTo[0],
                    limit: this.rankingsPerPage,
                    prepend: true,
                });
                this.paginated = true;
                this.paginatedUp = true;
            }

            this.lastScroll = e.target.scrollTop;
        },

        searchCleared() {
            this.searchedValue = '';
            this.searchedUsersList = [];
            this.userNotPartOfLeaderboard = false;
        },

        searchChanged(val) {
            if (timeout) {
                if (cancel) {
                    cancel();
                }
                clearTimeout(timeout);
            }

            this.searchedValue = val;

            if (this.searchedUsersList.length) {
                this.searchedUsersList = [];
            }

            if (val.length >= 3 && val.length <= 16) {
                this.searchLoading = true;
                timeout = setTimeout(async() => {

                    const { data } = await api.searchUsersBySimilarUsername(
                        {
                            similarUsername: val,
                        }, {},
                        {
                            ...prepareEDTOHeaders([
                                {
                                    at: '/data/*/externalUserId',
                                    fetch: 'UserService:User:byExternalUserId',
                                    into: 'user',
                                },
                            ]),
                            cancelToken: new Axios.CancelToken((c) => {
                                cancel = c;
                            }),
                        }
                    );

                    this.searchLoading = false;

                    if (data) {
                        this.searchedUsersList = data.slice(0, 5);
                    }
                }, 0);
            }
        },

        async goToTop() {
            const scrollList = this.$refs.scroller;

            if (this.pageFromTo[0] > 1) {
                this.pageFromTo = [1, 1];

                await this.fetchLeaderboardRankings({
                    leaderboardId: this.leaderboardData.id,
                    page: this.pageFromTo[0],
                    limit: this.rankingsPerPage,
                });
            }

            scrollList.scrollTop = 0;
        },

        async findUserInLeaderboard(user = null) {
            if (user) {
                this.searchedUser = user;
            }

            if (!this.searchedUser) {
                return;
            }

            // Get the userRank
            await this.searchUserRank({
                chilizUserId: this.searchedUser.externalUserId,
                leaderboardId: this.leaderboardData.id,
            });

            this.hideSearchDropdown = true;
            this.isScrollToTopVisible = false;
            if (!this.getSpecifiedUserRank && !this.getSpecifiedUserRank?.id) {
                this.userNotPartOfLeaderboard = true;

                return;
            }

            this.userNotPartOfLeaderboard = false;

            // Find on which page the user is and fetch that page
            const calculatePage = 1 + Math.floor((this.getSpecifiedUserRank.rank - 1) / this.rankingsPerPage);

            this.pageFromTo = [calculatePage, calculatePage];
            await this.fetchLeaderboardRankings({
                leaderboardId: this.leaderboardData.id,
                page: this.pageFromTo[0],
                limit: this.rankingsPerPage,
            });
        },

        onCardTapped() {
            if (this.isConditionsApplied) {
                this.showParticipationConditionOrEncourage();

                return;
            }

            this.$emit('clicked');
        },


        showParticipationConditionOrEncourage() {
            const partner = this.leaderboardData.partner?.id ? this.partnerById(this.leaderboardData.partner.id) : null;
            const voteToken = partner?.currencyId ? this.getVoteTokenByCurrencyId(partner.currencyId) : {};
            const voteTokenOfferings = voteToken?.id ? this.voteTokenOfferingsByStatusAndTokenId(['opened', 'published'], voteToken.id) : [];

            if (voteToken.currencyId) {
                const {
                    balance = 0,
                    lockedBalance = 0,
                } = this.decentralizedLedgerByToken(voteToken.currencyId);

                const userTokens = (Number(balance) + Number(lockedBalance));

                if (this.isFreemiumLeaderboardsEnabled || userTokens > 0) {
                    // In another task we will display another modal for the case when user has enoguh tokens, but hasn't participated
                    this.setModal({
                        isVisible: true,
                        isClosable: true,
                        componentToLoad: 'other-leaderboard-encouragement',
                    });

                    return;
                }
            }

            this.setModal({
                isVisible: true,
                componentToLoad: 'components/modals/not-enough-tokens',
                data: {
                    text: this.$t('marketplace.tokenBalance.fanTokensNeeded'),
                    message: this.$t('fanTokenNeededError.leaderboardsMessage', {
                        shortName: partner?.shortName ?? 'SSU',
                    }),
                    symbol: partner?.shortName ?? 'SSU',
                    balance: 0,
                    amount: 1,
                    action: () => {
                        if (this.marketByPartnerId(partner.id)) {
                            this.$router.push({
                                path: `/buy-ft/${partner.id}`,
                            });
                        } else if (voteTokenOfferings.length) {
                            this.$router.push({
                                path: `/buy-fto/${partner.id}`,
                            });
                        }
                    },
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
    .leaderboard-card {
        display: flex;
        flex-direction: column;
        background: $white-color;
        border-radius: 10px;
        padding: 1rem;
        position: relative;
        box-shadow: 0 1rem 1rem rgba(0, 0, 0, 0.08);

        @include min-width(md) {
            padding: 1.4rem 3rem;
        }

        &__leaderboard-header {
            margin-bottom: 1.5rem;

            &--add-margin-bottom {
                margin-bottom: 2rem;
            }
        }

        &__search-section {
            position: relative;
            margin-bottom: 4rem;
            z-index: 1;

            .search-result {
                background: rgba($color: $athens-gray-color, $alpha: 0.9);
                color: $daintree-color;
                padding: 4.5rem 1.5rem 1rem;
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                border-radius: 1rem;
                box-shadow: 0 7px 10px -3px rgba($color: $black-color, $alpha: 0.5);

                .user,
                .not-found {
                    display: grid;
                    grid-template-columns: 4.5rem 1fr;
                    grid-template-rows: 1fr 1fr;
                    gap: 0 0;
                    grid-template-areas:
                        "user-avatar title"
                        "user-avatar subtitle";
                    padding: 1rem;
                    border-radius: 1rem;
                }

                .user:hover {
                    cursor: pointer;
                    background-color: rgba($white-color, 0.75);
                }

                .user-avatar {
                    grid-area: user-avatar;

                    .avatar {
                        margin-right: 1rem;
                    }

                    .user-not-found {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 3rem;
                        height: 3rem;
                        border-radius: 50%;
                        color: $red-orange-color;
                        border: 1px solid $red-orange-color;
                        background: $white-color;
                        font-size: 1.6rem;
                    }
                }

                .title {
                    grid-area: title;
                    text-align: left;
                    font-weight: 700;
                    font-size: 1.5rem;
                    grid-column: 2;
                }

                .subtitle {
                    grid-area: subtitle;
                    text-align: left;
                    font-weight: 400;
                }
            }
        }

        &__participant-item {
            margin: 0.6rem 0;

            &--blur {
                filter: blur(4px);
            }

            &--highlighted {
                background-color: $lima-color-opaque;
            }

            &--you {
                background-color: $alice-blue;
            }
        }

        &__bottom-gradient {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 7rem;
            background: linear-gradient(to bottom, rgba($white-color, 0) 0%, $white-color 100%);
        }

        &__user-late-rank {
            position: relative;
            background: $alice-blue;
            margin-top: -5.2rem;
        }

        &__no-participants {
            text-align: center;
            font-size: 1.8rem;
            padding: 3rem 0;
        }
    }

    .scroller {
        max-height: 40rem;
        overflow-y: scroll;
    }

    .to-top {
        position: absolute;
        left: 50%;
        top: 15rem;
        transform: translateX(-50%) translateY(-1.5rem);
        background-color: $lima-color;
        padding: 0.35rem 2rem;
        font-size: 1.6rem;
        font-weight: 700;
        border-radius: 2rem;
        box-shadow: 0 0.5rem 0.75rem $dusty-gray-color;
        opacity: 0;
        transition: opacity 0.2s ease-out, transform 0.2s ease-out;
        cursor: pointer;

        &--visible {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
        }
    }
</style>